import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Button, Typography, TextField, Grid, IconButton,
  Collapse, List, ListItem, ListItemText, Divider, Tooltip,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Alert
} from '@mui/material';
import {
  PlayArrow, Pause, RestartAlt, SkipNext,
  VolumeUp, VolumeOff, ExpandMore, ExpandLess, DeleteSweep,
  NotificationsActive, NotificationsOff, SelfImprovement,
  LocalDrink, Visibility, DirectionsWalk, Close
} from '@mui/icons-material';
import { saveToStorage, loadFromStorage } from './storageUtils';

export interface PomodoroLog {
  type: 'work' | 'break';
  startTime: string;
  endTime: string;
}

export interface TimerState {
  isRunning: boolean;
  isWorkSession: boolean;
  sessionStartedAt: string | null;
}

interface PomodoroTimerProps {
  onSessionComplete?: (type: 'work' | 'break') => void;
  onLogsChange?: (logs: PomodoroLog[]) => void;
  onTimerStateChange?: (state: TimerState) => void;
}

const WORK_COLOR = '#F59E0B';
const BREAK_COLOR = '#2e7d32';
const WORK_BG = 'rgba(245,158,11,0.06)';
const BREAK_BG = 'rgba(46,125,50,0.06)';
const WARN_COLOR = '#b45309';

const BREAK_PROMPTS = [
  { icon: <DirectionsWalk fontSize="small" />, text: 'Stand up and stretch' },
  { icon: <LocalDrink fontSize="small" />, text: 'Drink some water' },
  { icon: <Visibility fontSize="small" />, text: 'Look away from the screen for 20 seconds' },
  { icon: <SelfImprovement fontSize="small" />, text: 'Take a few slow breaths' },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

const PomodoroTimer: React.FC<PomodoroTimerProps> = ({
  onSessionComplete, onLogsChange, onTimerStateChange,
}) => {
  const [workDuration, setWorkDuration] = useState<number>(25);
  const [breakDuration, setBreakDuration] = useState<number>(5);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isWorkSession, setIsWorkSession] = useState<boolean>(true);
  const [timeLeft, setTimeLeft] = useState<number>(25 * 60);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [audioInitialized, setAudioInitialized] = useState<boolean>(false);
  const [audioFailed, setAudioFailed] = useState<boolean>(false);
  const [notifEnabled, setNotifEnabled] = useState<boolean>(false);
  const [notifDenied, setNotifDenied] = useState<boolean>(false);
  const [pomodoroLogs, setPomodoroLogs] = useState<PomodoroLog[]>([]);
  const [showLog, setShowLog] = useState<boolean>(false);
  const [sessionTransition, setSessionTransition] = useState<'toBreak' | 'toWork' | null>(null);
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [nearEndDismissed, setNearEndDismissed] = useState<boolean>(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sessionStartTimeRef = useRef<Date | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Refs for stable callbacks
  const isWorkSessionRef = useRef(isWorkSession);
  const workDurationRef = useRef(workDuration);
  const breakDurationRef = useRef(breakDuration);
  const soundEnabledRef = useRef(soundEnabled);
  const notifEnabledRef = useRef(notifEnabled);
  const audioInitializedRef = useRef(audioInitialized);
  const endTimestampRef = useRef(endTimestamp);

  isWorkSessionRef.current = isWorkSession;
  workDurationRef.current = workDuration;
  breakDurationRef.current = breakDuration;
  soundEnabledRef.current = soundEnabled;
  notifEnabledRef.current = notifEnabled;
  audioInitializedRef.current = audioInitialized;
  endTimestampRef.current = endTimestamp;

  // ── Restore persisted state ──────────────────────────────────────────────
  useEffect(() => {
    const s = loadFromStorage('pomodoroState');
    if (!s) return;

    setWorkDuration(s.workDuration ?? 25);
    setBreakDuration(s.breakDuration ?? 5);
    setSoundEnabled(s.soundEnabled ?? true);
    setPomodoroLogs(s.pomodoroLogs ?? []);

    if (s.isRunning && s.endTimestamp) {
      const remaining = Math.floor((s.endTimestamp - Date.now()) / 1000);
      if (remaining > 0) {
        setIsWorkSession(s.isWorkSession);
        setTimeLeft(remaining);
        setEndTimestamp(s.endTimestamp);
        setIsRunning(true);
        setIsPaused(false);
        sessionStartTimeRef.current = s.sessionStartTime ? new Date(s.sessionStartTime) : new Date();
      } else {
        setIsWorkSession(!s.isWorkSession);
        setTimeLeft((!s.isWorkSession ? s.workDuration : s.breakDuration) * 60);
        setIsRunning(false);
        setSessionTransition(s.isWorkSession ? 'toBreak' : 'toWork');
      }
    } else {
      setIsWorkSession(s.isWorkSession ?? true);
      setTimeLeft(s.timeLeft ?? 25 * 60);
      setIsPaused(s.isPaused ?? false);
      setIsRunning(false);
    }
  }, []);

  // ── Persist state ────────────────────────────────────────────────────────
  useEffect(() => {
    saveToStorage('pomodoroState', {
      workDuration, breakDuration, isWorkSession, timeLeft,
      soundEnabled, pomodoroLogs, isRunning, isPaused,
      endTimestamp,
      sessionStartTime: sessionStartTimeRef.current?.toISOString() ?? null,
    });
    onLogsChange?.(pomodoroLogs);
  }, [workDuration, breakDuration, isWorkSession, timeLeft, soundEnabled, pomodoroLogs, isRunning, isPaused, endTimestamp]);

  // ── Session-end handler ──────────────────────────────────────────────────
  const handleSessionEnd = useCallback(() => {
    if (soundEnabledRef.current && audioRef.current && audioInitializedRef.current) {
      audioRef.current.play().catch(() => {});
    }

    const wasWork = isWorkSessionRef.current;
    const msg = wasWork
      ? 'Focus session complete — time for a break.'
      : 'Break over — ready to focus again?';

    if (notifEnabledRef.current && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Music Admin Mondays', { body: msg, icon: '/favicon.ico' });
    }

    if (sessionStartTimeRef.current) {
      const log: PomodoroLog = {
        type: wasWork ? 'work' : 'break',
        startTime: sessionStartTimeRef.current.toISOString(),
        endTime: new Date().toISOString(),
      };
      setPomodoroLogs(prev => [...prev, log]);
      sessionStartTimeRef.current = null;
    }

    onSessionComplete?.(wasWork ? 'work' : 'break');
    setSessionTransition(wasWork ? 'toBreak' : 'toWork');

    const nextIsWork = !wasWork;
    const nextDuration = nextIsWork ? workDurationRef.current : breakDurationRef.current;
    setIsWorkSession(nextIsWork);
    setTimeLeft(nextDuration * 60);
    setEndTimestamp(null);
    setIsRunning(false);
    setIsPaused(false);
    setNearEndDismissed(false);

    onTimerStateChange?.({ isRunning: false, isWorkSession: nextIsWork, sessionStartedAt: null });
  }, [onSessionComplete, onTimerStateChange]);

  // ── Timestamp-based interval ─────────────────────────────────────────────
  useEffect(() => {
    if (!isRunning || endTimestamp === null) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const tick = () => {
      const remaining = Math.floor((endTimestampRef.current! - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(timerRef.current!);
        handleSessionEnd();
      } else {
        setTimeLeft(remaining);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 500);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRunning, endTimestamp, handleSessionEnd]);

  // ── Visibility change ────────────────────────────────────────────────────
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !isRunning || endTimestampRef.current === null) return;
      const remaining = Math.floor((endTimestampRef.current - Date.now()) / 1000);
      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        handleSessionEnd();
      } else {
        setTimeLeft(remaining);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isRunning, handleSessionEnd]);

  // ── Tab title ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isRunning) {
      document.title = `${formatTime(timeLeft)} — ${isWorkSession ? 'Focus' : 'Break'} | Music Admin`;
    } else {
      document.title = 'Music Admin Mondays';
    }
    return () => { document.title = 'ShowRunnr'; };
  }, [timeLeft, isRunning, isWorkSession]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Starts the timer from the current timeLeft — used by both Start button and transition dialog buttons
  const startTimer = (workSession?: boolean) => {
    const sessionIsWork = workSession ?? isWorkSession;
    if (soundEnabled && !audioInitialized && !audioFailed) initializeAudio();
    const ts = Date.now() + timeLeft * 1000;
    setEndTimestamp(ts);
    setIsRunning(true);
    setIsPaused(false);
    setNearEndDismissed(false);
    if (!isWorkSession !== !sessionIsWork) setIsWorkSession(sessionIsWork);
    sessionStartTimeRef.current = new Date();
    onTimerStateChange?.({ isRunning: true, isWorkSession: sessionIsWork, sessionStartedAt: new Date().toISOString() });
  };

  const totalDuration = (isWorkSession ? workDuration : breakDuration) * 60;
  const progress = Math.max(0, Math.min(1, timeLeft / totalDuration));
  const nearEnd = isRunning && timeLeft <= 120 && timeLeft > 0 && !nearEndDismissed;
  const accentColor = nearEnd ? WARN_COLOR : (isWorkSession ? WORK_COLOR : BREAK_COLOR);
  const bgColor = isWorkSession ? WORK_BG : BREAK_BG;

  const handleStartPause = () => {
    if (!isRunning) {
      startTimer();
    } else {
      // Pause
      if (endTimestamp !== null) {
        const remaining = Math.max(0, Math.floor((endTimestamp - Date.now()) / 1000));
        setTimeLeft(remaining);
      }
      if (sessionStartTimeRef.current) {
        const log: PomodoroLog = {
          type: isWorkSession ? 'work' : 'break',
          startTime: sessionStartTimeRef.current.toISOString(),
          endTime: new Date().toISOString(),
        };
        setPomodoroLogs(prev => [...prev, log]);
        sessionStartTimeRef.current = null;
      }
      setEndTimestamp(null);
      setIsRunning(false);
      setIsPaused(true);
      onTimerStateChange?.({ isRunning: false, isWorkSession, sessionStartedAt: null });
    }
  };

  const handleReset = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRunning(false);
    setIsPaused(false);
    setEndTimestamp(null);
    setIsWorkSession(true);
    setTimeLeft(workDuration * 60);
    setNearEndDismissed(false);
    sessionStartTimeRef.current = null;
    onTimerStateChange?.({ isRunning: false, isWorkSession: true, sessionStartedAt: null });
  };

  const handleSkip = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const next = !isWorkSession;
    const nextDuration = next ? workDuration : breakDuration;
    setIsWorkSession(next);
    setTimeLeft(nextDuration * 60);
    setNearEndDismissed(false);
    if (isRunning) {
      const ts = Date.now() + nextDuration * 60 * 1000;
      setEndTimestamp(ts);
      sessionStartTimeRef.current = new Date();
      onTimerStateChange?.({ isRunning: true, isWorkSession: next, sessionStartedAt: new Date().toISOString() });
    } else {
      setEndTimestamp(null);
      setIsPaused(false);
      sessionStartTimeRef.current = null;
      onTimerStateChange?.({ isRunning: false, isWorkSession: next, sessionStartedAt: null });
    }
  };

  const initializeAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => {
      audioRef.current!.pause();
      audioRef.current!.currentTime = 0;
      setAudioInitialized(true);
      setAudioFailed(false);
    }).catch(() => {
      setAudioFailed(true);
    });
  };

  const handleNotifToggle = async () => {
    if (!notifEnabled) {
      if (!('Notification' in window)) {
        setNotifDenied(true);
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotifEnabled(true);
        setNotifDenied(false);
      } else {
        setNotifDenied(true);
      }
    } else {
      setNotifEnabled(false);
    }
  };

  const recentLogs = pomodoroLogs.slice(-5);

  return (
    <Box
      sx={{
        borderRadius: 3,
        background: bgColor,
        border: `1.5px solid ${accentColor}33`,
        transition: reducedMotion ? 'none' : 'background 0.6s ease, border-color 0.6s ease',
        p: 3,
      }}
    >
      <audio ref={audioRef} preload="auto">
        <source src="/alert.mp3" type="audio/mpeg" />
        <source src="/alert.ogg" type="audio/ogg" />
      </audio>

      {/* Audio failed warning */}
      {audioFailed && soundEnabled && (
        <Alert
          severity="warning"
          sx={{ mb: 2, fontSize: '0.8rem' }}
          action={
            <Button size="small" onClick={() => setSoundEnabled(false)}>Dismiss</Button>
          }
        >
          Sound couldn't be set up in this browser. Enable browser notifications (bell icon) instead so you still get an alert when your session ends.
        </Alert>
      )}

      {/* Notification denied warning */}
      {notifDenied && (
        <Alert
          severity="warning"
          onClose={() => setNotifDenied(false)}
          sx={{ mb: 2, fontSize: '0.8rem' }}
        >
          Browser notifications are blocked for this site. To enable them, click the lock icon in your browser's address bar and allow notifications.
        </Alert>
      )}

      {/* Session badge */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <Chip
          label={isWorkSession ? 'Focus time' : 'Break time'}
          size="small"
          sx={{
            bgcolor: accentColor, color: '#fff', fontWeight: 700,
            letterSpacing: 1, fontSize: '0.7rem',
            transition: reducedMotion ? 'none' : 'background 0.6s ease',
          }}
        />
      </Box>

      {/* Circular progress */}
      <Box sx={{ position: 'relative', width: 220, height: 220, mx: 'auto', mb: 1 }}>
        <svg width={220} height={220}>
          <circle cx={110} cy={110} r={100} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth={10} />
          <circle
            cx={110} cy={110} r={100}
            fill="none"
            stroke={accentColor}
            strokeWidth={nearEnd ? 13 : 10}
            strokeDasharray={2 * Math.PI * 100}
            strokeDashoffset={2 * Math.PI * 100 * (1 - progress)}
            strokeLinecap="round"
            transform="rotate(-90 110 110)"
            style={{ transition: reducedMotion ? 'none' : 'stroke-dashoffset 0.8s ease, stroke 0.6s ease, stroke-width 0.4s ease' }}
          />
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '3rem', fontWeight: 800, lineHeight: 1, color: accentColor, transition: reducedMotion ? 'none' : 'color 0.6s ease' }}>
            {formatTime(timeLeft)}
          </Typography>

          {/* State label — always clear about what the timer is doing */}
          {isPaused && !nearEnd && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 2, mt: 0.5, textTransform: 'uppercase' }}>
              Paused
            </Typography>
          )}
          {!isRunning && !isPaused && (
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, mt: 0.5 }}>
              {Math.round((1 - progress) * 100)}% done
            </Typography>
          )}
          {isRunning && !nearEnd && (
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 1, mt: 0.5 }}>
              {Math.round((1 - progress) * 100)}% done
            </Typography>
          )}
          {nearEnd && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography variant="caption" sx={{ color: WARN_COLOR, fontWeight: 700, letterSpacing: 0.5 }}>
                Almost done — wrap up soon
              </Typography>
              <Tooltip title="Dismiss warning">
                <IconButton size="small" onClick={() => setNearEndDismissed(true)} sx={{ p: 0, color: WARN_COLOR }}>
                  <Close sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Tooltip title={soundEnabled ? (audioFailed ? 'Sound unavailable' : 'Mute sound') : 'Enable sound'}>
          <IconButton
            size="small"
            onClick={() => { setSoundEnabled(s => !s); if (!soundEnabled && !audioInitialized && !audioFailed) initializeAudio(); }}
            sx={{ color: audioFailed ? 'warning.main' : 'text.secondary' }}
          >
            {soundEnabled && !audioFailed ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={notifEnabled ? 'Disable browser notifications' : 'Enable browser notifications'}>
          <IconButton size="small" onClick={handleNotifToggle} sx={{ color: 'text.secondary' }}>
            {notifEnabled ? <NotificationsActive fontSize="small" /> : <NotificationsOff fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Button
          variant="contained" size="large"
          startIcon={isRunning ? <Pause /> : <PlayArrow />}
          onClick={handleStartPause}
          sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' }, borderRadius: 8, px: 3, transition: reducedMotion ? 'none' : 'background 0.6s ease' }}
        >
          {isRunning ? 'Pause' : isPaused ? 'Resume' : 'Start'}
        </Button>

        <Tooltip title="Reset to start">
          <IconButton onClick={handleReset} sx={{ color: 'text.secondary' }}>
            <RestartAlt />
          </IconButton>
        </Tooltip>

        <Tooltip title="Skip to next session">
          <IconButton onClick={handleSkip} sx={{ color: 'text.secondary' }}>
            <SkipNext />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Duration settings */}
      <Grid container spacing={2} sx={{ mb: 0.5 }}>
        <Grid item xs={6}>
          <TextField label="Focus (min)" type="number" value={workDuration}
            onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0) { setWorkDuration(v); if (isWorkSession && !isRunning) setTimeLeft(v * 60); } }}
            disabled={isRunning} fullWidth size="small" inputProps={{ min: 1 }} />
        </Grid>
        <Grid item xs={6}>
          <TextField label="Break (min)" type="number" value={breakDuration}
            onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v) && v > 0) { setBreakDuration(v); if (!isWorkSession && !isRunning) setTimeLeft(v * 60); } }}
            disabled={isRunning} fullWidth size="small" inputProps={{ min: 1 }} />
        </Grid>
      </Grid>
      {isRunning && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
          Duration settings locked while running
        </Typography>
      )}

      {/* Session log */}
      {pomodoroLogs.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Button size="small" startIcon={showLog ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowLog(s => !s)} sx={{ color: 'text.secondary', textTransform: 'none' }}>
              Session log ({pomodoroLogs.length})
            </Button>
            <Tooltip title="Clear log">
              <IconButton size="small" onClick={() => setPomodoroLogs([])} sx={{ color: 'text.secondary' }}>
                <DeleteSweep fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={showLog}>
            <List dense disablePadding>
              {recentLogs.map((log, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: log.type === 'work' ? WORK_COLOR : BREAK_COLOR, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{log.type === 'work' ? 'Focus' : 'Break'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {new Date(log.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  } />
                </ListItem>
              ))}
              {pomodoroLogs.length > 5 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                  +{pomodoroLogs.length - 5} earlier entries
                </Typography>
              )}
            </List>
          </Collapse>
        </>
      )}

      {/* Session transition dialog */}
      <Dialog
        open={sessionTransition !== null}
        onClose={() => setSessionTransition(null)}
        maxWidth="xs"
        fullWidth
        // Prevent closing by clicking backdrop — user must make a deliberate choice
        disableEscapeKeyDown
      >
        {sessionTransition === 'toBreak' ? (
          <>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Good work. Time for a break.</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                Your {breakDuration}-minute break timer will start when you click below. Step away from the screen.
              </Typography>
              <Stack spacing={1.5}>
                {BREAK_PROMPTS.map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: BREAK_COLOR, flexShrink: 0 }}>{p.icon}</Box>
                    <Typography variant="body2" color="text.secondary">{p.text}</Typography>
                  </Box>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => { setSessionTransition(null); startTimer(); }}
                sx={{ bgcolor: BREAK_COLOR, '&:hover': { bgcolor: BREAK_COLOR, filter: 'brightness(0.9)' } }}
              >
                Start my {breakDuration}-minute break
              </Button>
              <Button
                fullWidth
                variant="text"
                sx={{ color: 'text.secondary' }}
                onClick={() => setSessionTransition(null)}
              >
                Skip break and stay on page
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Break over — ready when you are</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                No rush. Take a breath, pick a goal from your list, and click below to start your next {workDuration}-minute focus session.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => { setSessionTransition(null); startTimer(); }}
                sx={{ bgcolor: WORK_COLOR, '&:hover': { bgcolor: WORK_COLOR, filter: 'brightness(0.9)' } }}
              >
                Start {workDuration}-minute focus session
              </Button>
              <Button
                fullWidth
                variant="text"
                sx={{ color: 'text.secondary' }}
                onClick={() => setSessionTransition(null)}
              >
                Dismiss — I'll start manually
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PomodoroTimer;
